import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  CampaignMilestone,
  CampaignSimulatorParams,
  CampaignSimulatorResponse,
  FeatureTypes,
  TimelineDisplayMode,
  WorkspaceDTO,
} from '../../../shared/interfaces';
import { HelperService } from '../../../shared/services/helper.service';
import { TimelineService } from '../../../shared/services/timeline.service';
import { AuthService } from '../../../shared/services/auth.service';
import { DateHelperService } from '../../../shared/services/date-helper.service';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { OverlayService } from '../../../shared/services/overlayService';
import { ProjectV2ServiceService } from '../../../shared/services/project-v2-service.service';
import { WorkspaceService } from '../../../shared/services/workspace.service';
import { CryptoService } from '../../../shared/services/crypto.service';
import { Router } from '@angular/router';
import { DownloadTimelineComponent } from '../recap/download-timeline/download-timeline.component';
import { NgbDateParserFormatter, NgbDateStruct, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from '../../../shared/services/datepicker-adapter';
import { FeatureConfigService } from '../../../shared/services/feature-config.service';
import {
  CAMPAIGN_WORKSPACE_PROJECT_TYPES,
  CAMPAIGN_WORKSPACE_ROUTES,
} from '../../../shared/models/campaign-workspace-translations';
import { TIMELINE_SIMULATOR_FORM2_CONSTANTS } from './models/timeline-criteria-form2-constants';
import { SimulatorCriteriaForm2ExtractorService } from './services/simulator-criteria-form2-extractor.service';
import { SimulatorCriteriaExtractor } from './services/simulator-criteria-extractor.interface';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { FormHelperService } from '../../../shared/services/form-helper.service';
import { LogService } from '../../../shared/services/log.service';

@Component({
  selector: 'mtm-timeline-simulator',
  templateUrl: './timeline-simulator.component.html',
  styleUrl: './timeline-simulator.component.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    SimulatorCriteriaForm2ExtractorService,
  ],
  standalone: false,
})
export class TimelineSimulatorComponent implements OnInit, OnDestroy {
  private readonly timelineService = inject(TimelineService);
  private readonly authService = inject(AuthService);
  private readonly overlayService = inject(OverlayService);
  private readonly projectV2ServiceService = inject(ProjectV2ServiceService);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly cryptoService = inject(CryptoService);
  private readonly router = inject(Router);
  private readonly modalService = inject(NgbModal);
  private readonly featureConfigService = inject(FeatureConfigService);
  private readonly form2ExtractorService = inject(SimulatorCriteriaForm2ExtractorService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly formHelperService: FormHelperService = inject(FormHelperService);
  private readonly helperService: HelperService = inject(HelperService);
  private readonly dateHelperService: DateHelperService = inject(DateHelperService);
  private readonly logger: LogService = inject(LogService);
  criteriaFormType: string | null = null;
  expectedMad: NgbDateStruct | null = null;
  isInvalidMad: boolean = false;
  minDate: NgbDateStruct = { year: 2010, month: 1, day: 1 };
  maxDate: NgbDateStruct = { year: 2050, month: 12, day: 31 };
  protected readonly TIMELINE_SIMULATOR_FORM2_CONSTANTS: typeof TIMELINE_SIMULATOR_FORM2_CONSTANTS =
    TIMELINE_SIMULATOR_FORM2_CONSTANTS;
  protected readonly TimelineDisplayMode: typeof TimelineDisplayMode = TimelineDisplayMode;
  simulatorForm: FormGroup;
  timelineDisplayMode: TimelineDisplayMode = TimelineDisplayMode.VerticalList;
  timeline: CampaignMilestone[] = [];
  timelineToDisplay: CampaignMilestone[] = [];
  isLoading: boolean = false;
  modal: NgbModalRef;
  criteriaExtractor: SimulatorCriteriaExtractor;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_TIMELINE])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          const campaignTimelineConfig = response[FeatureTypes.FEA_CAMPAIGN_TIMELINE];

          if (campaignTimelineConfig) {
            const criteriaFormComponent: string | null = this.helperService.mapCriteriaFormTypeToComponentName(
              campaignTimelineConfig.criteriaForm
            );
            this.criteriaFormType = criteriaFormComponent;
            if (criteriaFormComponent == TIMELINE_SIMULATOR_FORM2_CONSTANTS.FORM_COMPONENT_NAME) {
              this.criteriaExtractor = this.form2ExtractorService;
            }
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public changeTimelineDisplay(value: TimelineDisplayMode): void {
    this.timelineDisplayMode = value;
  }

  private getCriteria(): Record<string, string | boolean | number> | null {
    if (!this.criteriaExtractor) {
      return null;
    }
    return this.criteriaExtractor.getCriteria(this.simulatorForm);
  }

  public async simulate(): Promise<void> {
    this.formHelperService.checkFormFields(this.simulatorForm);
    if (!this.simulatorForm.valid) {
      throw new Error('Form is not valid');
    }

    const criteria: Record<string, string | boolean | number> | null = this.getCriteria();
    if (!criteria) {
      throw new Error('Failed to extract criteria from form');
    }

    const payload: CampaignSimulatorParams = {
      expectedMad: this.dateHelperService.fromDateToApi(
        this.dateHelperService.fromDatePickerToDate(this.simulatorForm.value?.expectedMad)
      ),
      timelineCriteria: JSON.stringify(criteria),
      brandId: this.authService.getCurrentBrandId(),
    };

    this.resetTimelineState();
    this.isLoading = true;

    try {
      const response: CampaignSimulatorResponse = await firstValueFrom(
        this.timelineService.getTimelineSimulator(payload).pipe(takeUntil(this.ngUnsubscribe))
      );

      this.handleSimulationResponse(response);
    } catch (error) {
      this.handleSimulationError(error);
      throw error; // Re-throw to let caller handle it
    } finally {
      this.isLoading = false;
    }
  }

  private resetTimelineState(): void {
    this.timeline = [];
    this.timelineToDisplay = [];
  }

  private handleSimulationResponse(response: CampaignSimulatorResponse): void {
    if (!response?.timelines) {
      this.timeline = [];
      return;
    }

    try {
      const timeline = JSON.parse(response.timelines) || [];
      this.projectV2ServiceService.handleTimeLineDataSync(
        timeline,
        this.simulatorForm.value?.projectType === TIMELINE_SIMULATOR_FORM2_CONSTANTS.TIMELINE_CAMPAIGN_TYPES.HALLMARKS
      );
      this.timeline = timeline;
    } catch (parseError) {
      this.logger.logError(parseError, 'Failed to parse timeline data');
      this.overlayService.showError(this.translatePipe.transform('timelineSimulator_invalidTimelineData'));
      this.timeline = [];
      throw new Error('Timeline data parsing failed');
    }
  }

  private handleSimulationError(error: Error): void {
    this.logger.logError(error, 'Failed to generate timeline');
    this.overlayService.showError(this.translatePipe.transform('timelineSimulator_failedToGenerateTimeline'));
    this.timeline = [];
  }

  public goBack(): void {
    const activeWs: WorkspaceDTO = this.workspaceService.getActiveWorkspace();
    if (activeWs?.id) {
      const encryptedWsId: string = this.cryptoService.encryptString(activeWs.id);
      this.router.navigate([CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE], {
        queryParams: {
          wid: encryptedWsId,
        },
      });
      return;
    }
    this.router.navigate([CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE]);
  }

  private populateTimelineToDisplay(): void {
    const totalCampaignBlocks: number = this.timeline.filter(
      (item: CampaignMilestone) => item.name == CAMPAIGN_WORKSPACE_PROJECT_TYPES.CAMPAIGN
    ).length;
    const timelineToDisplay: CampaignMilestone[] = [...this.timeline];

    if (totalCampaignBlocks > 1) {
      const initialCampaignIndex: number = timelineToDisplay.findIndex(
        (item: CampaignMilestone) => item.name == CAMPAIGN_WORKSPACE_PROJECT_TYPES.CAMPAIGN
      );
      const initialCampaign: CampaignMilestone = timelineToDisplay[initialCampaignIndex];
      timelineToDisplay.splice(initialCampaignIndex, 1);
      const secondCampaignIndex: number = timelineToDisplay.findIndex(
        (item: CampaignMilestone) => item.name == CAMPAIGN_WORKSPACE_PROJECT_TYPES.CAMPAIGN
      );
      timelineToDisplay.splice(secondCampaignIndex, 0, initialCampaign);
    }

    this.timelineToDisplay = timelineToDisplay;
  }

  public async performSimulation(): Promise<void> {
    try {
      await this.simulate();
      this.populateTimelineToDisplay();
    } catch (error) {
      this.logger.logError(error);
    }
  }

  public async downloadTimeline(): Promise<void> {
    const criteria: string = JSON.stringify(this.getCriteria());

    try {
      await this.simulate();
      const timelines: string = JSON.stringify(this.timeline);
      const formValue: any = this.simulatorForm.value;
      formValue.expectedMad = this.dateHelperService.fromDateToApi(
        this.dateHelperService.fromDatePickerToDate(formValue?.expectedMad)
      );
      const timeline: any = { ...formValue, timelineCriteria: criteria, timelines };
      this.modal = this.modalService.open(DownloadTimelineComponent, { size: 'lg' });
      this.modal.componentInstance.timelineData = timeline;
      this.populateTimelineToDisplay();
    } catch (error) {
      this.logger.logError(error, 'Failed to download timeline');
    }
  }
}
